import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { RoleWidget } from "@/components/dashboard/RoleWidget";
import { JourneyLinkCard } from "@/components/dashboard/JourneyLinkCard";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { ActionCenterCard } from "@/components/dashboard/ActionCenterCard";
import { PhoneCard } from "@/components/dashboard/PhoneCard";
import { Sparkline } from "@/components/dashboard/Sparkline";
import { MilestoneEditDialog } from "@/components/admin/MilestoneEditDialog";
import { MilestoneBoard, nextStatus } from "@/components/milestones/MilestoneBoard";
import client from "@/api/client";
import { toast } from "sonner";
import {
  Folder, Building2, ClipboardList, FileText, Circle, ArrowRight, Rocket,
  TrendingUp, Flag, GraduationCap, Target, LifeBuoy,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, hint, testId, onNavigate }) {
  const clickable = !!onNavigate;
  return (
    <Card
      className={`rounded-2xl border border-border p-5 transition-all ${clickable ? "cursor-pointer hover:border-primary/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40" : ""}`}
      onClick={clickable ? onNavigate : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigate(); } } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open ${label}` : undefined}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="font-display text-3xl font-semibold mt-1">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </Card>
  );
}

/** The company headline card — first thing a member sees. */
function CompanyCard({ company, program, onSelect }) {
  const { t } = useTranslation();
  return (
    <Card className="rounded-2xl border border-border bg-card p-3.5 sm:p-6" data-testid="dashboard-company-card">
      {company ? (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2 sm:gap-4 sm:gap-y-3">
          <span className="flex h-9 w-9 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-primary/10 text-primary">
            <Building2 className="h-4 w-4 sm:h-6 sm:w-6" />
          </span>
          <div className="min-w-0 flex-1 basis-[60%]">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.companyTitle")}
            </p>
            <h2 className="font-display text-lg sm:text-3xl font-semibold tracking-tight mt-0.5 truncate leading-tight" data-testid="dashboard-company-name">
              {company.name}
            </h2>
            <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
              {[company.industry, company.region, company.stage].filter(Boolean).map((chip) => (
                <Badge key={chip} variant="outline" className="rounded-full text-[10px] capitalize">
                  {String(chip).replace(/_/g, " ")}
                </Badge>
              ))}
              <Badge variant="outline" className="rounded-full text-[10px]">
                {company.room_count} {t("adminCompanies.roomsShort")}
              </Badge>
              {program?.program && (
                <Badge className="max-w-full rounded-full bg-primary/12 text-primary border-primary/30 text-[10px] gap-1 overflow-hidden" data-testid="dashboard-company-program">
                  <Rocket className="h-2.5 w-2.5 shrink-0" />
                  <span dir="auto" className="truncate text-start">{program.program.name}</span>
                </Badge>
              )}
              {program?.company?.graduated_at && (
                <Badge className="rounded-full bg-emerald-500/12 text-emerald-700 border-emerald-500/30 text-[10px] gap-1">
                  <GraduationCap className="h-2.5 w-2.5" /> {t("dashboard.graduated", { defaultValue: "Graduated" })}
                </Badge>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto order-last sm:order-none flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-xl gap-2" onClick={() => onSelect("profile")} data-testid="dashboard-company-profile-cta">
              {t("dashboard.profile")} <ArrowRight className="h-3.5 w-3.5 rtl-flip" />
            </Button>
            <Link
              to="/dashboard?tab=tickets&prefill=rename_company"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline shrink-0"
              data-testid="dashboard-company-rename-link"
            >
              <LifeBuoy className="h-3 w-3" />
              {t("dashboard.changeCompanyName", { defaultValue: "Change company name?" })}
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 sm:gap-4">
          <span className="flex h-9 w-9 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-muted text-muted-foreground">
            <Building2 className="h-4 w-4 sm:h-6 sm:w-6" />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.companyTitle")}
            </p>
            <p className="text-sm text-muted-foreground mt-1" data-testid="dashboard-company-empty">
              {t("dashboard.companyEmpty")}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Milestones the member actually owns — second card on the overview. */
function MilestonesCard({ program, reload }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!program || !program.linked) return null;

  const patch = async (m, fields) => {
    setBusy(m.entry_id);
    try {
      await client.patch(`/my-company/milestones/${m.entry_id}`, fields);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(null); }
  };

  const save = async (fields) => {
    setSaving(true);
    try {
      if (editing?.entry_id) {
        await client.patch(`/my-company/milestones/${editing.entry_id}`, fields);
        toast.success(t("milestones.updated", { defaultValue: "Milestone updated" }));
      } else {
        await client.post("/my-company/milestones", fields);
        toast.success(t("milestones.added", { defaultValue: "Milestone added" }));
      }
      setEditorOpen(false);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const remove = async (entry_id) => {
    setSaving(true);
    try {
      await client.delete(`/my-company/milestones/${entry_id}`);
      toast.success(t("milestones.removed", { defaultValue: "Milestone removed" }));
      setEditorOpen(false);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const stages = Array.from(new Set([
    ...(program.milestones || []).map((m) => (m.stage || "").trim().toLowerCase()).filter(Boolean),
    (program.program?.stage_default || "").trim().toLowerCase(),
  ].filter(Boolean))).sort();

  if (!program.program) {
    return (
      <Card className="rounded-2xl border border-border bg-card p-5 space-y-2" data-testid="dashboard-milestones-card">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold">{t("dashboard.programTitle")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("dashboard.programNotEnrolled")}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-xl h-8 gap-1.5 text-xs"
          onClick={() => navigate("/dashboard?tab=tickets&prefill=program_start")}
          data-testid="program-help-start-button"
        >
          <LifeBuoy className="h-3.5 w-3.5" />
          {t("milestones.helpStart", { defaultValue: "Help me start" })}
        </Button>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border border-border bg-card p-4 sm:p-6 space-y-4" data-testid="dashboard-milestones-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Target className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-base md:text-lg font-semibold leading-tight">
            {t("milestones.myTitle", { defaultValue: "Milestones to complete" })}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{program.program.name}</p>
        </div>
        {program.milestone_stats?.overdue > 0 && (
          <Badge variant="outline" className="ms-auto rounded-full border-rose-500/40 bg-rose-500/10 text-rose-600 text-[10px]" data-testid="dashboard-milestones-overdue">
            {program.milestone_stats.overdue} {t("milestones.overdue", { defaultValue: "overdue" })}
          </Badge>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={`rounded-xl h-8 gap-1.5 text-xs ${program.milestone_stats?.overdue > 0 ? "" : "ms-auto"}`}
          onClick={() => navigate("/dashboard?tab=tickets&prefill=program_start")}
          data-testid="program-help-start-button"
        >
          <LifeBuoy className="h-3.5 w-3.5" />
          {t("milestones.helpStart", { defaultValue: "Help me start" })}
        </Button>
      </div>

      <MilestoneBoard
        milestones={program.milestones || []}
        stats={program.milestone_stats}
        busyKey={busy}
        onCycle={(m) => patch(m, { status: nextStatus(m.status) })}
        onNote={(m, note) => patch(m, { note })}
        onEdit={(m) => { setEditing(m); setEditorOpen(true); }}
        onAdd={() => { setEditing(null); setEditorOpen(true); }}
        canReorder={false}
      />

      <MilestoneEditDialog
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditing(null); }}
        milestone={editing}
        onSave={save}
        onDelete={remove}
        saving={saving}
        availableStages={stages}
      />
    </Card>
  );
}

/** KPI logging + assigned mentors — the reporting half of the program. */
function ProgramReportingCard({ program, reload }) {
  const { t } = useTranslation();
  const [showKpi, setShowKpi] = useState(false);
  const [busy, setBusy] = useState(null);
  const [kpi, setKpi] = useState({ month: new Date().toISOString().slice(0, 7), mrr: "", users: "", burn: "", runway_months: "", notes: "" });

  if (!program || !program.linked || !program.program) return null;

  const submitKpi = async (e) => {
    e?.preventDefault?.();
    setBusy("kpi");
    try {
      await client.post("/my-company/kpis", {
        month: kpi.month,
        mrr: Number(kpi.mrr || 0),
        users: Number(kpi.users || 0),
        burn: Number(kpi.burn || 0),
        runway_months: Number(kpi.runway_months || 0),
        notes: kpi.notes,
      });
      toast.success(t("dashboard.kpiSaved"));
      setShowKpi(false);
      setKpi({ month: new Date().toISOString().slice(0, 7), mrr: "", users: "", burn: "", runway_months: "", notes: "" });
      await reload();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || t("common.error"));
    } finally { setBusy(null); }
  };

  return (
    <Card className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="dashboard-reporting-card">
      <div className="flex items-center gap-2 flex-wrap">
        <TrendingUp className="h-4 w-4 text-primary" />
        <h3 className="font-display font-semibold">{t("dashboard.kpiTitle")}</h3>
        <Button size="sm" variant="ghost" className="ms-auto rounded-lg" onClick={() => setShowKpi((s) => !s)} data-testid="dashboard-kpi-toggle">
          {showKpi ? t("common.cancel") : t("dashboard.kpiCta")}
        </Button>
      </div>

      {program.kpis?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dashboard-kpi-sparks">
          {[
            { key: "mrr", label: "MRR", invert: false },
            { key: "users", label: "Users", invert: false },
            { key: "burn", label: "Burn", invert: true },
            { key: "runway_months", label: "Runway (mo)", invert: false },
          ].map((m) => {
            const series = program.kpis.slice().reverse().map((k) => Number(k[m.key]) || 0);
            const latest = series[series.length - 1];
            const first = series[0];
            const deltaPct = first ? Math.round(((latest - first) / Math.abs(first)) * 100) : 0;
            return (
              <div key={m.key} className="min-w-0 rounded-xl border border-border p-3" data-testid={`dashboard-kpi-spark-${m.key}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</p>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="font-mono text-sm">
                    {m.key === "users" ? latest.toLocaleString() : latest.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </span>
                  {series.length > 1 && (
                    <span className={`text-[10px] font-mono ${(m.invert ? deltaPct <= 0 : deltaPct >= 0) ? "text-emerald-500" : "text-rose-500"}`}>
                      {deltaPct > 0 ? "+" : ""}{deltaPct}%
                    </span>
                  )}
                </div>
                <div className="mt-1"><Sparkline values={series} width={140} height={26} invert={m.invert} /></div>
              </div>
            );
          })}
        </div>
      )}

      {showKpi && (
        <form onSubmit={submitKpi} className="grid gap-2 sm:grid-cols-4">
          <Input type="month" value={kpi.month} onChange={(e) => setKpi({ ...kpi, month: e.target.value })} className="rounded-lg" data-testid="dashboard-kpi-month" />
          <Input value={kpi.mrr} onChange={(e) => setKpi({ ...kpi, mrr: e.target.value })} placeholder="MRR" className="rounded-lg" data-testid="dashboard-kpi-mrr" />
          <Input value={kpi.users} onChange={(e) => setKpi({ ...kpi, users: e.target.value })} placeholder="Users" className="rounded-lg" />
          <Input value={kpi.burn} onChange={(e) => setKpi({ ...kpi, burn: e.target.value })} placeholder="Burn" className="rounded-lg" />
          <Input value={kpi.notes} onChange={(e) => setKpi({ ...kpi, notes: e.target.value })} placeholder="Notes" className="rounded-lg sm:col-span-3" />
          <Button type="submit" className="btn-brand rounded-lg" disabled={busy === "kpi"} data-testid="dashboard-kpi-submit">
            {t("common.save")}
          </Button>
        </form>
      )}

      {program.mentors?.length > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("dashboard.yourMentors", { defaultValue: "Your mentors" })}
          </p>
          <div className="flex flex-wrap gap-2">
            {program.mentors.map((m) => (
              <Badge key={m.entry_id} variant="outline" className="rounded-full">
                {m.name || t("common.member", { defaultValue: "Member" })} · {m.label}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/** Founder's single next commitment — milestone deadline + overdue alarm. */
function FounderNextStep({ widget, overdue }) {
  const { t } = useTranslation();
  const next = widget?.next_milestone;
  if (!next && (!overdue || overdue.length === 0)) return null;
  return (
    <Card className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="dashboard-founder-next">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Flag className="h-4 w-4" />
        </span>
        <h3 className="font-display font-semibold">
          {t("memberOverview.founderNext", { defaultValue: "Your next milestone" })}
        </h3>
      </div>
      {next ? (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="dashboard-founder-next-milestone">
          <p className="text-sm font-medium truncate">{next.title}</p>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">
            {t("memberOverview.due", { defaultValue: "Due" })} {fmtDate(next.due_at)}
            {next.stage ? ` · ${next.stage.replace(/_/g, " ")}` : ""}
          </p>
        </div>
      ) : overdue && overdue.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t("memberOverview.clearOverdueFirst", { defaultValue: "Nothing scheduled ahead — clear what's past due first." })}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("memberOverview.noMilestone", { defaultValue: "No upcoming milestone scheduled." })}
        </p>
      )}
      {overdue && overdue.length > 0 && (
        <p className="text-xs text-rose-600 font-medium" data-testid="dashboard-founder-overdue-count">
          {overdue.length}{" "}
          {overdue.length === 1
            ? t("memberOverview.overdueMilestones", { defaultValue: "milestone past due" })
            : t("memberOverview.overdueMilestonesPlural", { defaultValue: "milestones past due" })}
        </p>
      )}
    </Card>
  );
}

export function DashboardOverviewTab({ data, onSelect }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const [program, setProgram] = useState(null);

  const loadProgram = useCallback(async () => {
    try {
      const res = await client.get("/my-company/program");
      setProgram(res.data);
    } catch {
      setProgram({ linked: false });
    }
  }, []);
  useEffect(() => { loadProgram(); }, [loadProgram]);

  const goRooms = useCallback(() => {
    document.querySelector('[data-testid="dashboard-rooms-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (!data) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
      </div>
    );
  }
  const { stats, rooms, company, upcoming_tasks } = data;

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <ActionCenterCard />

      <CompanyCard company={company} program={program} onSelect={onSelect} />

      <MilestonesCard program={program} reload={loadProgram} />

      <OnboardingChecklist />

      <div className="flex sm:grid overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 -mx-3 px-3 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        <div className="w-[62%] sm:w-auto snap-center flex-shrink-0">
          <StatCard icon={Folder} label={t("dashboard.statRooms")} value={stats.rooms} hint={t("dashboard.statRoomsHint")} testId="dashboard-stat-rooms" onNavigate={goRooms} />
        </div>
        <div className="w-[62%] sm:w-auto snap-center flex-shrink-0">
          <StatCard icon={FileText} label={t("dashboard.statDocs")} value={stats.documents} hint={t("dashboard.statDocsHint")} testId="dashboard-stat-docs" onNavigate={goRooms} />
        </div>
        <div className="w-[62%] sm:w-auto snap-center flex-shrink-0">
          <StatCard icon={ClipboardList} label={t("dashboard.statOpenTasks")} value={stats.open_tasks} hint={`${stats.done_tasks} ${t("dashboard.statDoneShort")}`} testId="dashboard-stat-tasks" onNavigate={() => onSelect("tasks")} />
        </div>
        <div className="w-[62%] sm:w-auto snap-center flex-shrink-0">
          <StatCard icon={Building2} label={t("dashboard.statCompany")} value={company ? "1" : "—"} hint={company ? company.name : t("dashboard.statCompanyHint")} testId="dashboard-stat-company" onNavigate={() => onSelect("profile")} />
        </div>
      </div>

      {!user?.phone && <PhoneCard />}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2 space-y-6">
          <Card className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-3" data-testid="dashboard-rooms-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-sm">{t("dashboard.roomsTitle")}</h3>
              <Link to="/data-rooms" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                {t("dashboard.viewAll")} <ArrowRight className="h-3 w-3 rtl-flip" />
              </Link>
            </div>
            {rooms.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="dashboard-rooms-empty">
                {t("dashboard.roomsEmpty")}
              </p>
            ) : (
              <div className="flex sm:grid overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:grid-cols-2 gap-2.5 -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {rooms.slice(0, 6).map((r) => (
                  <button
                    key={r.room_id}
                    type="button"
                    onClick={() => navigate(`/data-rooms/${r.room_id}`)}
                    className="w-[70%] sm:w-auto snap-center flex-shrink-0 text-left rounded-xl border border-border p-2.5 hover:border-primary/40 hover:shadow-sm transition-all"
                    data-testid={`dashboard-room-${r.room_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Folder className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-xs">{r.company_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
                          <span>{r.document_count} {t("dataRoom.docsShort")}</span>
                          {r.stage && (<><span>·</span><span className="capitalize">{r.stage.replace(/_/g, " ")}</span></>)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="dashboard-upcoming-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary" />
                <h3 className="font-display font-semibold">{t("dashboard.upcomingTitle")}</h3>
              </div>
              <button type="button" onClick={() => onSelect("tasks")} className="text-xs text-primary hover:underline" data-testid="dashboard-upcoming-viewall">
                {t("dashboard.viewAll")}
              </button>
            </div>
            {upcoming_tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="dashboard-upcoming-empty">{t("dashboard.upcomingEmpty")}</p>
            ) : (
              <ul className="divide-y divide-border">
                {upcoming_tasks.map((t2) => (
                  <li key={t2.task_id} className="flex items-center gap-3 py-2 text-sm">
                    <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{t2.title}</span>
                    {t2.due_at && <span className="text-xs text-muted-foreground font-mono">{fmtDate(t2.due_at)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <ProgramReportingCard program={program} reload={loadProgram} />
        </div>

        <div className="min-w-0 space-y-6">
          {role === "founder" && <FounderNextStep widget={data.role_widget} overdue={data.overdue_milestones} />}
          <RoleWidget widget={data.role_widget} />
          <JourneyLinkCard />
        </div>
      </div>

      {data.overdue_milestones && data.overdue_milestones.length > 0 && (
        <Card className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-5 space-y-3" data-testid="dashboard-overdue-card">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
            <h3 className="font-display font-semibold text-rose-600">
              {t("memberOverview.attention", { defaultValue: "Attention needed" })}
            </h3>
            <span className="ml-auto text-xs text-muted-foreground">
              {data.overdue_milestones.length} {t("memberOverview.overdue", { defaultValue: "overdue" })}
            </span>
          </div>
          <ul className="space-y-2">
            {data.overdue_milestones.map((m) => (
              <li key={m.entry_id} className="flex items-center gap-3 text-sm rounded-lg bg-background p-2" data-testid={`dashboard-overdue-${m.entry_id}`}>
                <Circle className="h-4 w-4 text-rose-500 flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate">{m.title}</span>
                <span className="text-xs font-mono text-rose-500">{t("memberOverview.due", { defaultValue: "Due" })} {fmtDate(m.due_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export default DashboardOverviewTab;
